import React, { useState, useEffect } from "react";
import "./Project.css";
import {
  addProject,
  updateProject,
  getProject,
  deleteProjetById,
  getTypologyData,
  getThemeData,
} from "../../../../services/CommonService";
import Swal from "sweetalert2";
import ProjectLatLongPreview from "./ProjectLatLongPreview";
import TipTapEditor from "../../../../Utils/TipTapEditor";
import ThemeTypoModal from "./ThemetypoModal";
import moment from "moment";
import { exportToExcel } from "../../../../Utils/common-Excel";
import usePagination from "../../../../Utils/usePagination";
import PaginationControls from "../../../../Utils/PaginationControls";

const Project = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    typology: [],
    state: [],
    district: [],
    start_date: "",
    end_date: "",
    status: "",
    project_head_details: "",
    theme: [],
    donor: "",
    locations: [{ lat: "", lng: "" }]
  });

  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [lookupData, setLookupData] = useState([]);
  const [masterData, setMasterData] = useState([]);
  const [themes, setThemes] = useState([]); // New state for themes
  const [typologies, setTypologies] = useState([]); // New state for typologies
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [selectionType, setSelectionType] = useState(null); // For view modal
  const [selectedData, setSelectedData] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectViewModal, setShowProjectViewModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDataReady, setIsDataReady] = useState(false);


  // Fetch existing projects data
  useEffect(() => {
    const loadData = () => {
      const lookupRaw = localStorage.getItem("lookup");
      const masterRaw = localStorage.getItem("StateMaster");

      if (!lookupRaw || !masterRaw) {
        console.warn("lookup or master data not found in localStorage");
        return;
      }

      try {
        const lookup = JSON.parse(lookupRaw);
        const master = JSON.parse(masterRaw);

        const sortedLookup = lookup
          .map((item) => ({ ...item }))
          .sort((a, b) => a.LookupValue?.localeCompare(b.LookupValue));

        const sortedMaster = {
          ...master,
          state: master.state?.sort((a, b) =>
            a.statename?.localeCompare(b.statename)
          ) || [],
          district: master.district?.sort((a, b) =>
            a.districtname?.localeCompare(b.districtname)
          ) || [],
        };

        setLookupData(sortedLookup);
        setMasterData(sortedMaster);
        setIsDataReady(true);
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: error?.response?.data?.message || error?.message,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };

    loadData();
    fetchThemes();
    fetchTypologies();
  }, []);

  useEffect(() => {
    if (isDataReady && masterData && lookupData) {
      fetchProjects();
    }
  }, [isDataReady, masterData, lookupData]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await getProject();

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const rawProjects = response?.data?.projects || [];

      const mappedProjects = rawProjects.map((project) => {
        const stateIds = Array.isArray(project.state)
          ? project.state
          : project?.state?.split(',');

        return {
          ...project,
          statename: getNamesByIds(
            stateIds,
            masterData?.state || [],
            'id_mststate',
            'statename'
          ),
          statusname: lookupData?.find(
            (l) => l.LookupCode == project.status && l.Flag === "13"
          )?.LookupValue
        };
      });

      setProjects(mappedProjects);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching projects:", error);
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      setLoading(false);
    }
  };

  // New API calls for themes and typologies
  const fetchThemes = async () => {
    try {
      const response = await getThemeData();
      if (response.status === 200) {
        // Sort themes by name in ascending order
        const sortedThemes = response.data.sort((a, b) => a.name?.localeCompare(b.name));
        setThemes(sortedThemes);
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const fetchTypologies = async () => {
    try {
      const response = await getTypologyData();
      if (response.status === 200) {
        // Sort typologies by name in ascending order
        const sortedTypologies = response.data.sort((a, b) => a.name?.localeCompare(b.name));
        setTypologies(sortedTypologies);
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // Validation function
  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) {
      errors.title = "Title is required";
    }
    if (!formData.state || formData.state.length === 0) {
      errors.state = "State is required";
    }
    if (!formData.status) {
      errors.status = "Status is required";
    }
    // Validate end date is after start date
    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      errors.end_date = "End date must be after start date";
    }
    if (formData.locations.some(loc => loc.lat && !loc.lng)) {
      errors.longitude = "Longitude is required";
    }

    if (formData.locations.some(loc => !loc.lat && loc.lng)) {
      errors.lat = "Latitude is required";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null
      });
    }
  };

  const handleLocationChange = (index, field, value) => {
    const updated = [...formData.locations];
    updated[index][field] = value;
    setFormData({ ...formData, locations: updated });
  };

  const addRow = () => {
    setFormData({
      ...formData,
      locations: [...formData.locations, { lat: "", lng: "" }]
    });
  };

  const removeRow = (index) => {
    const updated = formData.locations.filter((_, i) => i !== index);
    setFormData({ ...formData, locations: updated });
  };

  // Handle multiple select change
  const handleMultiSelectChange = (e, fieldName) => {
    const value = Array.from(e.target.selectedOptions, (option) => option.value);
    setFormData({
      ...formData,
      [fieldName]: value,
    });

    // Clear validation error
    if (validationErrors[fieldName]) {
      setValidationErrors({
        ...validationErrors,
        [fieldName]: null
      });
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData({
      ...formData,
      [name]: files[0],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        title: "Validation Error!",
        text: "Please fill all required fields correctly",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    setLoading(true);

    try {
      // ✅ Create FormData instead of JSON
      const formPayload = new FormData();

      // Basic fields
      formPayload.append("title", formData.title);
      formPayload.append("description", formData.description);
      formPayload.append("typology", formData.typology.join(","));
      formPayload.append("state", formData.state.join(","));
      formPayload.append("district", formData.district.join(","));
      formPayload.append("start_date", formData.start_date);
      formPayload.append("end_date", formData.end_date);
      formPayload.append("status", formData.status);
      formPayload.append("project_head_details", formData.project_head_details);
      formPayload.append("theme", formData.theme.join(","));
      formPayload.append("donor", formData.donor || "");

      // ✅ MULTIPLE LOCATIONS (IMPORTANT PART)
      if (formData.locations && formData.locations.length > 0) {
        formData.locations.forEach((loc, index) => {
          if (loc.lat && loc.lng) {
            formPayload.append(`locations[${index}][lat]`, loc.lat);
            formPayload.append(`locations[${index}][lng]`, loc.lng);
          }
        });
      }

      // ✅ File uploads
      if (formData.banner) {
        formPayload.append("banner", formData.banner);
      }

      if (formData.media) {
        formPayload.append("media", formData.media);
      }

      let response;

      if (selectedProjectId) {
        response = await updateProject(formPayload, selectedProjectId);
        if (response.status !== 200) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
      } else {
        response = await addProject(formPayload);
        if (response.status !== 201) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
      }

      Swal.fire({
        title: "Success!",
        text: selectedProjectId
          ? "Project updated successfully!"
          : "Project created successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });

      resetForm();
      fetchProjects();

    } catch (error) {
      console.error("Error handling project:", error);
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  // Sorting functionality
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedProjects = React.useMemo(() => {
    let sortableProjects = [...projects];
    if (sortConfig.key !== null) {
      sortableProjects.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableProjects;
  }, [projects, sortConfig]);

  const getSortIcon = (columnName) => {
    if (sortConfig.key === columnName) {
      return sortConfig.direction === 'ascending' ? '↑' : '↓';
    }
    return '↕';
  };

  const loadProjectData = (project) => {
    setFormData({
      title: project.title || "",
      description: project.description || "",
      typology: Array.isArray(project.typology) ? project.typology : (project.typology ? project.typology?.split(',') : []),
      state: Array.isArray(project.state) ? project.state : (project.state ? project.state?.split(',') : []),
      district: Array.isArray(project.district) ? project.district : (project.district ? project.district?.split(',') : []),
      start_date: project.start_date || "",
      end_date: project.end_date || "",
      status: project.status || "",
      project_head_details: project.project_head_details || "",
      theme: Array.isArray(project.theme) ? project.theme : (project.theme ? project.theme?.split(',') : []),
      donor: project.donor || "",
      locations: project.locations && project.locations.length > 0
        ? project.locations.map(loc => ({
          lat: loc.latitude || "",
          lng: loc.longitude || ""
        }))
        : [{ lat: "", lng: "" }]
    });
    setSelectedProjectId(project.id);
    setShowForm(true);
  };

  const viewThemeTypo = (type) => {
    setSelectionType(type);
    setShowViewModal(true);
  };

  const viewProjectDetails = (project) => {
    setSelectedProject(project);
    setShowProjectViewModal(true);
  };

  const deleteProjectData = (project) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${project.title}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await deleteProjetById(project.id);
          if (res.status == 200) {
            Swal.fire('Deleted!', 'Project has been deleted.', 'success');
            fetchProjects();
          }

        } catch (err) {
          Swal.fire({
            title: "Error",
            text: err?.response?.data?.message || err?.message,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      typology: [],
      state: [],
      district: [],
      start_date: "",
      end_date: "",
      status: "",
      project_head_details: "",
      theme: [],
      donor: "",
      locations: [{ lat: "", lng: "" }]
    });
    setSelectedProjectId(null);
    setShowForm(false);
    setValidationErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowForm(true);
  };

  // Helper function to get names from IDs
  const getNamesByIds = (ids, dataArray, idField, nameField) => {
    if (!Array.isArray(ids) || ids.length === 0) return '';
    return ids.map(id => {
      const item = dataArray.find(item => item[idField] == id);
      return item ? item[nameField] : id;
    }).join(', ');
  };

  const handleDownloadExcel = () => {
    const exportData = sortedProjects?.map((item, index) => ({
      'S.No': index + 1,
      Title: item.title,
      'Start Date': dateFormat(item.start_date),
      'End Date': dateFormat(item.end_date),
      Theme: getNamesByIds(
        Array.isArray(item.theme) ? item.theme : item?.theme?.split(','),
        themes,
        'id',
        'theme'
      ),
      Typology: getNamesByIds(
        Array.isArray(item.typology) ? item.typology : item?.typology?.split(','),
        typologies,
        'id',
        'typology'
      ),
      State: getNamesByIds(
        Array.isArray(item.state) ? item.state : item?.state?.split(','),
        masterData?.state || [],
        'id_mststate',
        'statename'
      ),
      District: getNamesByIds(
        Array.isArray(item.district) ? item.district : item?.district?.split(','),
        masterData?.district || [],
        'id_mstdistrict',
        'districtname'
      ),
      Status: (lookupData?.length > 0) && (item.status ? lookupData?.find(m => m.LookupCode == item.status && m.Flag === "13")?.LookupValue : ''),
      Donor: item.donor,
      'Project Head Details': item.project_head_details,
      Latitude: item.lat,
      longitude: item.longitude,
      Description: stripHtml(item.description),
      'Created on': dateFormat(item.created_at),
      'Updated on': dateFormat(item.updated_at)
    }));

    exportToExcel(exportData, 'Projects', 'Projects', 'Project Management', null, null);
  };

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  // Search filter
  const filteredProject = sortedProjects.filter(proj => {
    const matchesSearch = searchTerm
      ? (proj?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase())) ||
      (proj?.statename?.toLowerCase()?.includes(searchTerm?.toLowerCase())) ||
      (proj?.statusname?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
      : true;
    return matchesSearch;
  });

  const { currentPage, setCurrentPage, totalPages, paginatedData: filteredData } = usePagination(filteredProject, itemsPerPage);

  const dateFormat = (d) => (d ? moment(d).format('DD-MM-YYYY') : 'N/A');

  return (
    <div className="admin-portal-container project">
      {!showForm ? (
        <div className="container-fluid">
          <div className="card mb-4">
            <div className="card-header adminCardHeader d-flex justify-content-between align-items-center">
              <h4>Projects Management</h4>
              <button
                type="button"
                className="btn btn-sm btn-warning"
                onClick={openAddModal}
              >
                <i className="bi bi-plus-circle me-1"></i> Add New Project
              </button>
            </div>
            <div className="card-body">
              <div className="row text-end mb-2">
                <div className="col-md-10">
                  <div className="d-flex justify-content-start align-items-center">
                    <label className="me-2">Search:</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1); // Reset to first page
                      }}
                      style={{ width: '150px' }}
                    />
                  </div>
                </div>
                <div className="col-md-2 " >
                  <button className="btn btn-success mb-2 " onClick={handleDownloadExcel}>
                    <i className="bi bi-file-earmark-excel-fill me-2"></i>
                    Excel
                  </button>
                </div>
              </div>
              {loading && <p className="text-center">Loading projects...</p>}

              {projects.length === 0 && !loading ? (
                <p className="text-center">No projects found.</p>
              ) : (

                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th className="text-center">S.No</th>
                        <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                          Title {getSortIcon('title')}
                        </th>
                        <th onClick={() => handleSort('state')} style={{ cursor: 'pointer' }}>
                          Location {getSortIcon('state')}
                        </th>
                        <th onClick={() => handleSort('start_date')} style={{ cursor: 'pointer' }}>
                          Duration {getSortIcon('start_date')}
                        </th>
                        <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                          Status {getSortIcon('status')}
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(filteredData || [])?.map((project, index) => (
                        <tr key={project.id}>
                          <td className="text-center">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                          <td>{project.title}</td>
                          <td>
                            {project.statename}
                          </td>
                          <td>
                            {dateFormat(project.start_date)} to {dateFormat(project.end_date)}
                          </td>
                          <td>
                            <span
                              className={`badge ${project.statusname === "Completed"
                                ? "bg-success"
                                : project.statusname === "Ongoing"
                                  ? "bg-primary"
                                  : "bg-warning"
                                }`}
                            >
                              {project.statusname}
                            </span>
                          </td>
                          <td>
                            <i
                              className="bi bi-pencil-fill text-primary me-2"
                              onClick={() => loadProjectData(project)}
                              style={{ cursor: 'pointer' }}
                              title="Edit"
                            ></i>
                            <i
                              className="bi bi-trash-fill text-danger"
                              onClick={() => deleteProjectData(project)}
                              style={{ cursor: 'pointer' }}
                              title="Delete"
                            ></i>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Pagination Controls */}
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={setCurrentPage}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                totalItems={filteredProject?.length}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="container-fluid">
          <div className="card mb-4">
            <div className="card-header adminCardHeader d-flex justify-content-between align-items-center">
              <h4>
                {selectedProjectId ? "Update Project" : "Add New Project"}
              </h4>
              <button
                type="button"
                className="btn btn-sm btn-warning ms-2"
                onClick={resetForm}
              >
                <i className="bi bi-arrow-left"></i> Back
              </button>
            </div>
            <div className="card-body proj-mbody">
              <form onSubmit={handleSubmit} id="projectForm">
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label htmlFor="title" className="form-label">
                      Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.title ? 'is-invalid' : ''}`}
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                    />
                    {validationErrors.title && (
                      <div className="invalid-feedback">{validationErrors.title}</div>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="theme" className="form-label">
                      Theme
                    </label><i className="bi bi-info-circle text-primary ms-2" onClick={() => {
                      viewThemeTypo('Theme');
                      setSelectedData(themes)
                    }
                    } title="View Theme Info" style={{ cursor: 'pointer' }}></i>
                    <div className="dropdown multiBtnSelect form-select">
                      <button
                        className="btn multiBtn py-0 btn-outline-secondary dropdown-toggle w-100 text-start"
                        type="button"
                        id="themeDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        {formData.theme.length > 0
                          ? `${formData.theme.length} theme(s) selected`
                          : 'Select Themes'
                        }
                      </button>
                      <ul className="dropdown-menu w-100" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {themes?.sort((a, b) => a?.theme?.localeCompare(b?.theme))?.map((theme) => (
                          <li key={theme.id}>
                            <label className="dropdown-item">
                              <input
                                type="checkbox"
                                className="form-check-input me-2"
                                checked={formData.theme.includes(theme.id.toString())}
                                onChange={(e) => {
                                  const value = theme.id.toString();
                                  const newValues = e.target.checked
                                    ? [...formData.theme, value]
                                    : formData.theme.filter(id => id !== value);
                                  setFormData({ ...formData, theme: newValues });
                                }}
                              />
                              {theme.theme}
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="typology" className="form-label">
                      Typology
                    </label><i className="bi bi-info-circle text-primary ms-2" onClick={() => {
                      viewThemeTypo('Typology');
                      setSelectedData(typologies)
                    }
                    } title="View Typology Info" style={{ cursor: 'pointer' }}></i>
                    <div className="dropdown multiBtnSelect form-select">
                      <button
                        className="btn multiBtn py-0 btn-outline-secondary dropdown-toggle w-100 text-start"
                        type="button"
                        id="typologyDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        {formData.typology.length > 0
                          ? `${formData.typology.length} typology(s) selected`
                          : 'Select Typologies'
                        }
                      </button>
                      <ul className="dropdown-menu w-100" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {typologies?.sort((a, b) => a?.typology?.localeCompare(b?.typology))?.map((typology) => (
                          <li key={typology.id}>
                            <label className="dropdown-item">
                              <input
                                type="checkbox"
                                className="form-check-input me-2"
                                checked={formData.typology.includes(typology.id.toString())}
                                onChange={(e) => {
                                  const value = typology.id.toString();
                                  const newValues = e.target.checked
                                    ? [...formData.typology, value]
                                    : formData.typology.filter(id => id !== value);
                                  setFormData({ ...formData, typology: newValues });
                                }}
                              />
                              {typology.typology}
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="state" className="form-label">
                      State <span className="text-danger">*</span>
                    </label>
                    <div className="dropdown multiBtnSelect form-select">
                      <button
                        className={`btn multiBtn py-0 btn-outline-secondary dropdown-toggle w-100 text-start ${validationErrors.state ? 'border-danger' : ''}`}
                        type="button"
                        id="stateDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        {formData.state.length > 0
                          ? `${formData.state.length} state(s) selected`
                          : 'Select States'
                        }
                      </button>
                      <ul className="dropdown-menu w-100" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {
                          (masterData?.state?.length > 0) && masterData?.state?.map((m) => (
                            <li key={m.id_mststate}>
                              <label className="dropdown-item">
                                <input
                                  type="checkbox"
                                  className="form-check-input me-2"
                                  checked={formData.state.includes(m.id_mststate.toString())}
                                  onChange={(e) => {
                                    const value = m.id_mststate.toString();
                                    const newValues = e.target.checked
                                      ? [...formData.state, value]
                                      : formData.state.filter(id => id !== value);
                                    setFormData({ ...formData, state: newValues, district: [] }); // Clear districts when states change
                                    if (validationErrors.state) {
                                      setValidationErrors({ ...validationErrors, state: null });
                                    }
                                  }}
                                />
                                {m.statename}
                              </label>
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                    {validationErrors.state && (
                      <div className="text-danger small mt-1">{validationErrors.state}</div>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="district" className="form-label">
                      District
                    </label>
                    <div className="dropdown multiBtnSelect form-select">
                      <button
                        className="btn multiBtn py-0 btn-outline-secondary dropdown-toggle w-100 text-start"
                        type="button"
                        id="districtDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        disabled={!formData.state || formData.state.length === 0}
                      >
                        {formData.district.length > 0
                          ? `${formData.district.length} district(s) selected`
                          : 'Select Districts'
                        }
                      </button>
                      <ul className="dropdown-menu w-100" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {
                          (masterData?.district?.length > 0) && masterData?.district?.filter(m =>
                            formData.state.includes(m.id_mststate.toString())
                          )?.map((m) => (
                            <li key={m.id_mstdistrict}>
                              <label className="dropdown-item">
                                <input
                                  type="checkbox"
                                  className="form-check-input me-2"
                                  checked={formData.district.includes(m.id_mstdistrict.toString())}
                                  onChange={(e) => {
                                    const value = m.id_mstdistrict.toString();
                                    const newValues = e.target.checked
                                      ? [...formData.district, value]
                                      : formData.district.filter(id => id !== value);
                                    setFormData({ ...formData, district: newValues });
                                  }}
                                />
                                {m.districtname}
                              </label>
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="start_date" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      className={`form-control date`}
                      id="start_date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      required
                    />
                    {validationErrors.start_date && (
                      <div className="invalid-feedback">{validationErrors.start_date}</div>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="end_date" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      className={`form-control date`}
                      id="end_date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleInputChange}
                      required
                    />
                    {validationErrors.end_date && (
                      <div className="invalid-feedback">{validationErrors.end_date}</div>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="project_head_details" className="form-label">
                    Project Head Details
                  </label>
                  <textarea
                    className="form-control"
                    id="project_head_details"
                    name="project_head_details"
                    rows="3"
                    value={formData.project_head_details}
                    onChange={handleInputChange}
                  ></textarea>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="donor" className="form-label">
                      Donor
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="donor"
                      name="donor"
                      value={formData.donor}
                      onChange={handleInputChange}
                    />
                    {selectedProjectId && (
                      <small className="form-text text-muted">
                        Upload a new file to change the current donor
                      </small>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="status" className="form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${validationErrors.status ? 'is-invalid' : ''}`}
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Status</option>
                      {
                        (lookupData?.length > 0) && lookupData?.sort((a, b) => (a.Sequence - b.Sequence))
                          .filter(m => m.Flag === "13")?.map((m) => (
                            <option key={m.LookupCode} value={m.LookupCode}>{m.LookupValue}</option>
                          ))
                      }
                    </select>
                    {validationErrors.status && (
                      <div className="invalid-feedback">{validationErrors.status}</div>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <TipTapEditor
                    value={formData.description}
                    onChange={(content) => {
                      setFormData({ ...formData, description: content });
                    }}
                    isGrayMode={false}
                  />
                </div>
                {formData?.locations?.map((loc, index) => (
                  <div className="row mb-2" key={index}>
                    <div className="col-md-5">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Latitude"
                        value={loc.lat}
                        onChange={(e) =>
                          handleLocationChange(index, "lat", e.target.value)
                        }
                      />
                    </div>

                    <div className="col-md-5">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Longitude"
                        value={loc.lng}
                        onChange={(e) =>
                          handleLocationChange(index, "lng", e.target.value)
                        }
                      />
                    </div>

                    <div className="col-md-2 d-flex gap-1">
                      <button type="button" onClick={addRow} className="btn btn-success">
                        +
                      </button>

                      {formData.locations.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRow(index)}
                          className="btn btn-danger"
                        >
                          -
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <ProjectLatLongPreview
                  locations={formData.locations}
                  onLocationChange={(index, { lat, lng }) => {
                    const updated = [...formData.locations];

                    const latNum = Number(lat);
                    const lngNum = Number(lng);

                    updated[index] = {
                      lat: !isNaN(latNum) ? latNum.toFixed(6) : "",
                      lng: !isNaN(lngNum) ? lngNum.toFixed(6) : "",
                    };

                    setFormData((prev) => ({
                      ...prev,
                      locations: updated,
                    }));
                  }}
                />
              </form>
            </div>
            <div className="text-end">
              <button
                type="button"
                className="btn btn-sm btn-warning m-3"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading
                  ? "Processing..."
                  : selectedProjectId
                    ? "Update Project"
                    : "Add Project"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Project Modal */}
      {showProjectViewModal && selectedProject && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Project Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowProjectViewModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6><strong>Title:</strong> {selectedProject.title}</h6>
                    <h6><strong>Status:</strong>
                      <span className={`badge ms-2 ${selectedProject.status === 3 ? "bg-success" : selectedProject.status === 2 ? "bg-primary" : "bg-warning"}`}>
                        {(lookupData?.length > 0) && (selectedProject.status ? lookupData?.find(m => m.LookupCode == selectedProject.status && m.Flag === "13")?.LookupValue : '')}
                      </span>
                    </h6>
                    <h6><strong>Duration:</strong> {dateFormat(selectedProject.start_date)} to {dateFormat(selectedProject.end_date)}</h6>
                    <h6><strong>States:</strong> {getNamesByIds(
                      Array.isArray(selectedProject.state) ? selectedProject.state : selectedProject.state ? selectedProject.state?.split(',') : '',
                      masterData?.state || [],
                      'id_mststate',
                      'statename'
                    )}</h6>
                    <h6><strong>Districts:</strong> {getNamesByIds(
                      Array.isArray(selectedProject.district) ? selectedProject.district : selectedProject.district ? selectedProject.district?.split(',') : '',
                      masterData?.district || [],
                      'id_mstdistrict',
                      'districtname'
                    )}</h6>
                  </div>
                  <div className="col-md-6">
                    <h6><strong>Themes:</strong> {getNamesByIds(
                      Array.isArray(selectedProject.theme) ? selectedProject.theme : selectedProject.theme ? selectedProject.theme?.split(',') : '',
                      themes,
                      'id',
                      'theme'
                    )}</h6>
                    <h6><strong>Typologies:</strong> {getNamesByIds(
                      Array.isArray(selectedProject.typology) ? selectedProject.typology : selectedProject.typology ? selectedProject.typology?.split(',') : '',
                      typologies,
                      'id',
                      'typology'
                    )}</h6>
                    <h6><strong>Donor:</strong> {selectedProject.donor}</h6>
                    <h6><strong>Coordinates:</strong> {selectedProject.lat}, {selectedProject.longitude}</h6>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-12">
                    <h6><strong>Project Head Details:</strong></h6>
                    <p>{selectedProject.project_head_details}</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h6><strong>Description:</strong></h6>
                    <div dangerouslySetInnerHTML={{ __html: selectedProject.description }} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowProjectViewModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && (
        <ThemeTypoModal selectionType={selectionType} selectedData={selectedData} setShowViewModal={setShowViewModal} />
      )}
    </div>
  );
};

export default Project;